import authAxios, { authKeepAlive } from "../utils/authAxios";

const practiceClient = {
    initializePractice: async (practiceRequest) => {
        return await authAxios(`/api/practice/init`, {
            method: "POST",
            // Пояс идёт вместе с запросом: «сегодня» — это дневной лимит новых и
            // сегодняшняя доля плана, и в UTC оба числа неверны у половины часовых
            // поясов. Явно переданный в запросе пояс имеет приоритет.
            data: { tzOffsetMinutes: practiceClient.tzOffsetMinutes(), ...practiceRequest },
            headers: {
                "Content-Type": "application/json",
            },
        });
    },

    submitPracticeResult: async (practiceResult) => {
        return await authAxios(`/api/practice`, {
            method: 'POST',
            data: practiceResult,
            headers: {
                'Content-Type': 'application/json',
            },
        });
    },

    // Окно вопросов: пачка сразу, вместо запроса на каждый показанный вопрос.
    getWindow: async ({ practiceId, learningMethod, size }) => {
        return await authAxios(`/api/practice/window`, {
            method: 'POST',
            data: { practiceId, learningMethod, size },
            headers: { 'Content-Type': 'application/json' },
        });
    },

    // Пачка ответов. Шлём факты — что и когда ответили; счётчики считает сервер.
    submitEvents: async ({ practiceId, learningMethod, events }) => {
        return await authAxios(`/api/practice/events`, {
            method: 'POST',
            data: { practiceId, learningMethod, events },
            headers: { 'Content-Type': 'application/json' },
        });
    },

    // Та же пачка, но запросом, который переживает закрытие вкладки. Обычный запрос
    // браузер убивает вместе со страницей, и последние ответы занятия не доходили.
    submitEventsOnLeave: async ({ practiceId, learningMethod, events }) => {
        return await authKeepAlive(`/api/practice/events`, { practiceId, learningMethod, events });
    },

    // Сводка практик колоды для страницы выбора. Только чтение — в отличие от init,
    // который создаёт практику, добирает пул и сохраняет документ.
    getDeckPracticeSummary: async (deckId, practiceType) => {
        return await authAxios(`/api/decks/${deckId}/practices/summary?practiceType=${practiceType}`, {
            method: 'GET',
        });
    },

    getPracticeStats: async (practiceId, page = 0, size = 500) => {
        return await authAxios(`/api/practice/${practiceId}/stats?page=${page}&size=${size}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        });
    },

    // Новый метод для получения практики по идентификатору
    getPracticeById: async (practiceId) => {
        return await authAxios(`/api/practice/${practiceId}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });
    },

    // Настройки обучения пользователя: как много он хочет помнить и сколько нового
    // вводить в день. Отдаются всегда заполненными, поэтому пустого слайдера не бывает.
    getLearningSettings: async () => {
        return await authAxios('/api/learning/settings', { method: 'GET' });
    },

    updateLearningSettings: async (settings) => {
        return await authAxios('/api/learning/settings', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            data: settings,
        });
    },

    // Методика колоды. Одна на колоду: след памяти у элемента один на все форматы, и
    // две политики назначали бы ему два срока.
    getDeckLearningMethod: async (deckId, practiceType) => {
        return await authAxios(
            `/api/learning/decks/${deckId}/method?practiceType=${practiceType}`, { method: 'GET' });
    },

    // Возвращает, скольким элементам пересчитали срок: переключение применяется сразу.
    setDeckLearningMethod: async (deckId, learningMethod) => {
        return await authAxios(`/api/learning/decks/${deckId}/method`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            data: { learningMethod },
        });
    },

    // Как колода выглядит под выбранной методикой: подписанные корзины, смысл которых
    // задаёт метод — коробки, сроки или число верных.
    getDeckLearningStats: async (deckId, practiceType) => {
        return await authAxios(
            `/api/learning/decks/${deckId}/stats?practiceType=${practiceType}`, { method: 'GET' });
    },

    // План к дате: считается на сервере при каждом запросе, потому что вчерашнее число
    // сегодня уже неверно — часть работы сделана.
    // Пояс ученика в минутах от UTC. Без него «сегодня» считалось бы по серверу: в
    // UTC+13 дневная доля обнулялась бы среди дня, а в UTC−8 вечернее занятие попадало
    // бы в завтра. getTimezoneOffset даёт минуты до UTC, то есть с обратным знаком.
    tzOffsetMinutes: () => -new Date().getTimezoneOffset(),

    getDeckDeadline: async (deckId, practiceType) => {
        return await authAxios(
            `/api/learning/decks/${deckId}/deadline?practiceType=${practiceType}`
            + `&tzOffsetMinutes=${practiceClient.tzOffsetMinutes()}`, { method: 'GET' });
    },

    setDeckDeadline: async (deckId, practiceType, examOn) => {
        return await authAxios(
            `/api/learning/decks/${deckId}/deadline?practiceType=${practiceType}`
            + `&tzOffsetMinutes=${practiceClient.tzOffsetMinutes()}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                data: { examOn },
            });
    },

    clearDeckDeadline: async (deckId, practiceType) => {
        return await authAxios(
            `/api/learning/decks/${deckId}/deadline?practiceType=${practiceType}`
            + `&tzOffsetMinutes=${practiceClient.tzOffsetMinutes()}`, { method: 'DELETE' });
    },

    // Пробный экзамен: отдельная сущность, не практика. Меряет и не двигает расписание,
    // поэтому и живёт по своему адресу.
    startExam: async (request) => {
        return await authAxios('/api/exams', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: request,
        });
    },

    getExam: async (examId) => {
        return await authAxios(`/api/exams/${examId}`, { method: 'GET' });
    },

    answerExam: async (examId, answer) => {
        return await authAxios(`/api/exams/${examId}/answers`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            data: answer,
        });
    },

    finishExam: async (examId) => {
        return await authAxios(`/api/exams/${examId}/finish`, { method: 'POST' });
    },

    // Постранично: элементы колоды с их состоянием памяти. Порядок — столбец и
    // направление, фильтры — по одному параметру каждый; пустой фильтр не отправляется.
    getDeckLearningItems: async (deckId, practiceType, {
        sort = 'DUE', direction = 'ASC', page = 0, size = 25,
        leech, overdue, box, rangeStart, rangeEnd, notStarted,
    } = {}) => {
        const params = new URLSearchParams({ practiceType, sort, direction, page, size });
        Object.entries({ leech, overdue, box, rangeStart, rangeEnd, notStarted })
            .filter(([, value]) => value !== undefined && value !== null && value !== false && value !== '')
            .forEach(([key, value]) => params.set(key, value));
        return await authAxios(`/api/learning/decks/${deckId}/items?${params}`, { method: 'GET' });
    },
};

export default practiceClient;
